import PropTypes from 'prop-types'
import { Autocomplete, Avatar, Card, Stack, TextField, Typography } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'

import { stringToColor } from '../../utils/cssStyles'
import { useLocales } from '../../locales'

PartnerCard.propTypes = {
  partners: PropTypes.array,
  onChangePartner: PropTypes.func,
  sx: PropTypes.object,
}

const customFilterOptions = (options, state) => {
  const query = (state.inputValue || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()

  return options.filter((option) => {
    const labelNormalized = (option.label || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
    return labelNormalized.includes(query)
  })
}

export default function PartnerCard({ partners = [], onChangePartner, sx, ...other }) {
  const [value, setValue] = useState(null)
  const [selectedPartner, setSelectedPartner] = useState(null)

  const { translate } = useLocales()

  const options = useMemo(() => {
    const mapped = partners
      .filter((item) => item && item.id)
      .map((item) => ({
        id: item.id,
        label: `${item?.name || ''} | ${item?.email || ''}`,
      }))

    const uniqueMap = new Map()
    for (const opt of mapped) {
      if (!uniqueMap.has(opt.label)) {
        uniqueMap.set(opt.label, opt)
      }
    }
    return Array.from(uniqueMap.values())
  }, [partners])

  const handleChange = useCallback(
    (event, newValue) => {
      const partner = partners.find((p) => p.id === newValue?.id)

      setSelectedPartner(partner || null)
      setValue(newValue)

      onChangePartner?.(event, newValue)
    },
    [partners, onChangePartner]
  )

  return (
    <Stack sx={{ mb: 2, ...sx }} {...other}>
      <Autocomplete
        options={options}
        value={value}
        onChange={handleChange}
        filterOptions={customFilterOptions}
        isOptionEqualToValue={(option, currentValue) => option.id === currentValue?.id}
        getOptionLabel={(option) => option?.label || ''}
        renderInput={(params) => (
          <TextField {...params} label={translate('impersonate.selectPartner')} />
        )}
      />

      {selectedPartner && (
        <Card
          sx={{
            p: 2,
            mt: 1,
            width: 1,
            maxWidth: '100%',
            mx: 'auto',
            boxShadow: 0,
            border: (theme) => `solid 1px ${theme.palette.divider}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: stringToColor(selectedPartner.email),
              }}
            >
              {selectedPartner.name?.charAt(0)?.toUpperCase()}
            </Avatar>

            <div>
              <Typography variant="subtitle2">{selectedPartner.name}</Typography>

              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {translate('impersonate.mail')} : {selectedPartner.email}
                <br />
                {translate('listColumns.document')} : {selectedPartner.document}
              </Typography>
            </div>
          </Stack>
        </Card>
      )}
    </Stack>
  )
}
